import { useReactiveVar } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnalytics } from '@treams/treams-analytics';
import { expectedUserVar } from '@treams/treams-hooks';

import { type GenderGap, buildGenderGap } from './buildGenderGap';
import {
	type RewardChange,
	type RewardFunction,
	type RewardPosition,
	type RewardScale,
	type RewardTitle,
	type RewardUser,
	buildRewardFunctions,
} from './buildRewardFunctions';

const EMPTY_PIPELINE: Record<string, unknown>[] = [];

export function useRewardAnalytics(): {
	loading: boolean;
	functions?: RewardFunction[];
	genderGap?: GenderGap;
} {
	const { i18n, t } = useTranslation();
	const viewerID = useReactiveVar(expectedUserVar);
	const { requestQuery, lastSyncedAt } = useAnalytics();
	const [result, setResult] = useState<{
		functions: RewardFunction[];
		genderGap?: GenderGap;
	}>();
	const [failedSync, setFailedSync] = useState<string>();

	useEffect(() => {
		let cancelled = false;

		Promise.all([
			requestQuery('Users', EMPTY_PIPELINE),
			requestQuery('JobPositions', EMPTY_PIPELINE),
			requestQuery('JobTitles', EMPTY_PIPELINE),
			requestQuery('SalaryScales', EMPTY_PIPELINE),
			requestQuery('SalaryChanges', EMPTY_PIPELINE),
		])
			.then(([users, positions, titles, scales, changes]) => {
				if (cancelled) {
					return;
				}

				const now = new Date();
				const noFunctionName = t('analytics.reward.genderGap.noFunction');

				setResult({
					functions: buildRewardFunctions({
						users: users as RewardUser[],
						positions: positions as RewardPosition[],
						titles: titles as RewardTitle[],
						scales: scales as RewardScale[],
						changes: changes as RewardChange[],
						now,
						language: i18n.language,
						noFunctionName,
						viewerID,
					}),
					genderGap: buildGenderGap({
						users: users as RewardUser[],
						positions: positions as RewardPosition[],
						titles: titles as RewardTitle[],
						changes: changes as RewardChange[],
						now,
						language: i18n.language,
						noFunctionName,
						totalName: t('analytics.reward.genderGap.total'),
						viewerID,
					}),
				});
			})
			.catch(() => {
				if (!cancelled) {
					setFailedSync(lastSyncedAt);
				}
			});

		return () => {
			cancelled = true;
		};
		// t changes identity every render. The fallback labels follow the language.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [requestQuery, lastSyncedAt, i18n.language, viewerID]);

	const loading = result === undefined && failedSync !== lastSyncedAt;

	return { loading, functions: result?.functions, genderGap: result?.genderGap };
}
